import { ButtonCusWithIcon, ButtonSearch } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import Tooltip from '@/components/common/Tooltip';
import Table from '@/components/table/Table';
import { DEFAULT_SELECT_PAGE_SIZES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { scrollToTop } from '@/utils/utils';
import { formatCurrency } from '@/utils/formatNumber';
import { yupResolver } from '@hookform/resolvers/yup';
import { TableState } from '@tanstack/react-table';
import { ColumnDef, Updater } from '@tanstack/react-table';
import { getMonth, getYear } from 'date-fns';
import { Pencil } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import IssueInvoiceForm from './components/IssueInvoiceForm';
import EditCommissionForm from './components/EditCommissionForm';
import { CommissionMonthOwner } from '@/types/commission';
import { CommissionMonthYearOwner } from '@/types/http-payload/commission';
import { monthsCombobox, yearsCombobox } from '@/utils/datetime';

export default observer((function CommissionOwnerPage() {
    // hook
    const { t } = useTranslation();

    // store
    const {
        accountStore: { accounts, getAccounts },
        commissionMonthStore: {
            commissionMonths,
            totalRecord,
            paging,
            keepSearchParams,
            getCommissionMonths,
            setObservable
        },
        modalStore: { showAlertModal, hideAllModals }
    } = useStore();

    // const
    const currentYear = getYear(new Date());
    const years = yearsCombobox();
    const currentMonth = getMonth(new Date()) + 1;
    const months = monthsCombobox;

    // form
    const validateSchema = yup.object().shape({
        year: yup.string(),
        month: yup.string(),
        ownerId: yup.string()
    });
    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            year: keepSearchParams?.year?.toString() ?? '',
            month: keepSearchParams?.month?.toString() ?? '',
            ownerId: keepSearchParams?.ownerId?.toString() ?? ''
        }
    });

    // columns
    const columns: ColumnDef<CommissionMonthOwner>[] = [
        {
            accessorKey: 'time',
            header: t('field.commission_recording_month'),
            size: 260,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <span>
                    {`${original.year}/${original.month}`}
                </span>
            )
        },
        {
            accessorKey: 'owner.fullname',
            header: t('field.owner'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='line-clamp-2 break-all min-w-[120px]'>{original?.owner?.fullname ?? ''}</div>
            )
        },
        {
            accessorKey: 'owner.code',
            header: t('field.user_code'),
            size: 100,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap min-w-[100px] line-clamp-2'>{original?.owner?.userCode ?? ''}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'totalCompensation',
            header: t('field.automobile_insurance_premiums_samall_motorcycles'),
            size: 200,
            enableSorting: false,
            meta: {
                headerClassName: 'right-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='text-right'>
                    {t('currency.currency_symbol')}
                    {formatCurrency(original.totalCompensation) || '0'}
                </div>
            )
        },
        {
            accessorKey: 'totalCommission',
            header: t('field.commisison_rate'),
            size: 280,
            enableSorting: false,
            meta: {
                headerClassName: 'right-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='text-right'>
                    {t('currency.currency_symbol')}
                    {formatCurrency(original.totalCommission) || '0'}
                </div>
            )
        },
        {
            accessorKey: 'totalCompensationAdjust',
            header: t('field.adjustment_amount'),
            size: 260,
            enableSorting: false,
            meta: {
                headerClassName: 'right-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => {
                const item = mapItem(original);
                let totalAdjust = Number((original.totalCompensationAdjust) || '0');
                let negativeNumber = totalAdjust < 0;

                return (
                    <div className='flex items-center justify-end gap-3'>
                        <p className='text-right'>
                            {negativeNumber && <span>&#9650;</span>}
                            {t('currency.currency_symbol')}
                            {formatCurrency(Math.abs(totalAdjust))}
                        </p>
                        <Tooltip content={t('button.edit')} triggerAsChild={true}>
                            <button
                                onClick={() => onEditCommission(item)}
                                className='size-8 rounded-full bg-background-4 flex items-center justify-center hover:bg-gray-200 transition-all cursor-pointer'
                            >
                                <Pencil className='size-4 text-icon-4' />
                            </button>
                        </Tooltip>
                    </div>
                );
            }
        }, {

            // id: 'actions',
            accessorKey: 'actions',
            header: '',
            size: 80,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const item = mapItem(original);
                return (
                    <ButtonCusWithIcon
                        type='button'
                        variant='orange3'
                        size='xsm'
                        onClick={() => onIssueInvoice(item)}
                        className='gap-1.5 w-fit px-2 whitespace-nowrap'
                        iconId='#icon-print'
                        widthIcon={19}
                        heightIcon={20}
                    >
                        {t('button.issuance_of_fee_list')}
                    </ButtonCusWithIcon>
                );
            }
        }
    ];

    // lifecycle
    useEffect(() => {
        // fetch data form filter
        getAccounts({ isOwner: true, page: -1, size: 10 });

        // fetch commissions
        onFetchData();
    }, []);

    // function
    const onFetchData = async () => {
        await flowResult(
            getCommissionMonths(
                keepSearchParams || {
                    year: currentYear,
                    month: currentMonth
                }
            )
        );

        if (!keepSearchParams) {
            setObservable(
                'keepSearchParams',
                { year: currentYear, month: currentMonth },
                { isMergeObject: false }
            );

            form.setValue('year', currentYear.toString());
            form.setValue('month', currentMonth.toString());
        }
    };

    const onSearch = (data: any) => {
        const dataSearch = { ...data };

        getCommissionMonths(dataSearch, {
            ...paging,
            pagination: { ...paging.pagination, pageIndex: 0 }
        });

        setObservable('keepSearchParams', dataSearch, { isMergeObject: false });
        hideAllModals();
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging =
            stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        getCommissionMonths(keepSearchParams, newPaging);
        scrollToTop();
    };

    const onRefresh = () => {
        onFetchData();
    };

    const onIssueInvoice = (item: CommissionMonthYearOwner) => {
        const idModal = 'issue-invoice-modal';
        showAlertModal({
            id: idModal,
            title: t('words_title.rental_bike_fee_table_issued'),
            size: '2xl',
            titleClassName: 'text-center max-w-none',
            content: (
                <IssueInvoiceForm idModal={idModal} adjust={item} isAdmin={true} />
            ),
            footerClassName: 'hidden'
        });
    };

    const onEditCommission = (item: CommissionMonthYearOwner) => {
        const idModal = 'edit-commission-modal';
        showAlertModal({
            id: idModal,
            title: t('field.adjustment_amount'),
            size: 'xl',
            titleClassName: 'text-center max-w-none',
            content: (
                <EditCommissionForm idModal={idModal} adjust={item} onRefresh={onRefresh} />
            ),
            footerClassName: 'hidden'
        });
    };

    const mapItem = (item): CommissionMonthYearOwner => {
        const commissionMonth = item?.month || '';
        const commissionYear = item?.year || '';
        const ownerId = item?.owner?.id || '';

        return {
            commissionMonth,
            commissionYear,
            ownerId
        };
    };

    return (
        <>
            <div className='layout-card'>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSearch)}>
                        <div className='grid grid-cols-1 point-375:grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6'>
                            <FormField
                                control={form.control}
                                name='ownerId'
                                render={({ field }) => (
                                    <FormItem className='col-span-full sm:col-span-1'>
                                        <FormControl>
                                            <Combobox
                                                placeholder={t('field.owner')}
                                                value={field.value ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.value ?? '');
                                                }}
                                                items={
                                                    (Array.isArray(accounts) && accounts?.length ?
                                                        [
                                                            ...accounts
                                                                .map(item => ({
                                                                    label: item.fullname,
                                                                    labelSearch: item.fullname + item?.userCode,
                                                                    value: item.id.toString(),
                                                                    code: item.id
                                                                }))
                                                        ] :
                                                        []
                                                    )
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='year'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Combobox
                                                placeholder={t('field.year')}
                                                value={field.value ? field.value.toString() : ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.value ?? '');
                                                }}
                                                search={false}
                                                items={years.map(year => ({
                                                    label: `${year}年`,
                                                    value: year.toString()
                                                }))}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='month'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Combobox
                                                placeholder={t('field.month')}
                                                value={field.value ? field.value.toString() : ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.value ?? '');
                                                }}
                                                search={false}
                                                items={months.map(month => ({
                                                    label: `${month}月`,
                                                    value: month.toString()
                                                }))}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className='col-span-full flex justify-center'>
                                <ButtonSearch size='md' className='max-w-52'>
                                    {t('button.search')}
                                </ButtonSearch>
                            </div>
                        </div>
                    </form>
                </Form>
            </div>

            <div className='layout-card mt-6'>
                <Table
                    columns={columns}
                    data={commissionMonths}
                    state={{
                        ...paging
                    }}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    enableRowSelection={false}
                // getRowId={row => row.id?.toString()}
                />
            </div>
        </>
    );
}));
